/**
 * Order Management React Query Hooks
 * 
 * Provides React Query hooks for order lifecycle management including:
 * - Fetching orders with filtering and pagination
 * - Creating, updating, and deleting orders
 * - Order statistics and KPIs
 * 
 * All hooks use React Query for automatic caching, background refetching,
 * and optimistic updates with proper cache invalidation.
 * 
 * @module hooks/useOrders
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orderService, api } from '../services/api';
import { API_ENDPOINTS } from '../config/api.config.js';

/**
 * Fetches orders with optional filtering
 * 
 * @param {Object} filters - Query parameters for filtering orders
 * @param {string} [filters.status] - Filter by order status
 * @param {string} [filters.customer_id] - Filter by customer
 * @param {string} [filters.warehouse_id] - Filter by warehouse
 * @param {number} [filters.page] - Page number for pagination
 * @param {number} [filters.limit] - Items per page
 * @returns {Object} React Query result with orders data
 * @property {Object} data - Orders response object
 * @property {Array} data.items - Array of order records
 * @property {number} data.total - Total number of orders
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useOrders = (filters = {}) => {
  return useQuery({
    queryKey: ['orders', filters],
    queryFn: async () => {
      try {
        const result = await orderService.getOrders(filters);
        return result || { items: [], orders: [], total: 0 };
      } catch {
        // Return empty data if endpoint fails (circuit breaker, 500 errors, etc.)
        return { items: [], orders: [], total: 0 };
      }
    },
    retry: false,
  });
};

/**
 * Fetches a single order by ID
 * 
 * Query is disabled if orderId is not provided
 * 
 * @param {string} orderId - Order ID
 * @returns {Object} React Query result with order data
 * @property {Object} data - Order record with full details
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useOrder = (orderId) => {
  return useQuery({
    queryKey: ['orders', orderId],
    queryFn: async () => {
      try {
        const result = await orderService.getOrderById(orderId);
        return result || null;
      } catch {
        // Return null if endpoint fails
        return null;
      }
    },
    enabled: !!orderId,
    retry: false,
  });
};

/**
 * Mutation hook to create a new order
 * 
 * Automatically invalidates orders query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {Function} mutateAsync - Async version of mutate
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const createOrder = useCreateOrder();
 * createOrder.mutate({ 
 *   customer_id: '123', 
 *   items: [...], 
 *   shipping_address: {...} 
 * });
 */
export const useCreateOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: orderService.createOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

/**
 * Mutation hook to update an existing order
 * 
 * Automatically invalidates both the specific order and orders list on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const updateOrder = useUpdateOrder();
 * updateOrder.mutate({ 
 *   orderId: '123', 
 *   data: { status: 'processing' } 
 * });
 */
export const useUpdateOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, data }) => orderService.updateOrder(orderId, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['orders', variables.orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

/**
 * Mutation hook to delete an order
 * 
 * Automatically invalidates orders query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const deleteOrder = useDeleteOrder();
 * deleteOrder.mutate('order_123');
 */
export const useDeleteOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: orderService.deleteOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

/**
 * Fetches order statistics and KPIs
 * 
 * @param {Object} params - Query parameters for filtering stats
 * @param {string} [params.start_date] - Start date for statistics period
 * @param {string} [params.end_date] - End date for statistics period
 * @param {string} [params.warehouse_id] - Filter by warehouse
 * @returns {Object} React Query result with order statistics
 * @property {Object} data - Order statistics object
 * @property {number} data.total_orders - Total number of orders
 * @property {number} data.pending_orders - Number of pending orders
 * @property {number} data.completed_orders - Number of completed orders
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useOrderStats = (params = {}) => {
  return useQuery({
    queryKey: ['orders', 'stats', params],
    queryFn: async () => {
      try {
        const result = await orderService.getOrderStats(params);
        return result || {};
      } catch {
        // Return empty stats if endpoint fails
        return {};
      }
    },
    retry: false,
  });
};

/**
 * Hook to get picking statistics
 * @returns {Object} Query result with picking statistics
 */
export const usePickingStats = () => {
  return useQuery({
    queryKey: ['orders', 'picking-stats'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.ORDERS}/picking-stats`);
      return data;
    },
  });
};

/**
 * Query hook to fetch order trends
 * 
 * @param {Object} params - Query parameters
 * @returns {Object} React Query result with order trends data
 */
export const useOrderTrends = (params = {}) => {
  return useQuery({
    queryKey: ['orders', 'trends', params],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.ORDERS}/trends`, { params });
      return response.data || {};
    },
  });
};

/**
 * Query hook to fetch order distribution
 * 
 * @param {Object} params - Query parameters
 * @returns {Object} React Query result with order distribution data
 */
export const useOrderDistribution = (params = {}) => {
  return useQuery({
    queryKey: ['orders', 'distribution', params],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.ORDERS}/distribution`, { params });
      return response.data || {};
    },
  });
};

/**
 * Query hook to fetch orders by marketplace
 * 
 * @param {Object} params - Query parameters
 * @returns {Object} React Query result with orders by marketplace data
 */
export const useOrdersByMarketplace = (params = {}) => {
  return useQuery({
    queryKey: ['orders', 'by-marketplace', params],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.ORDERS}/by-marketplace`, { params });
      return response.data || {};
    },
  });
};

/**
 * Query hook to fetch order performance metrics
 * 
 * @param {Object} params - Query parameters
 * @returns {Object} React Query result with performance metrics data
 */
export const useOrderPerformanceMetrics = (params = {}) => {
  return useQuery({
    queryKey: ['orders', 'performance-metrics', params],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.ORDERS}/performance-metrics`, { params });
      return response.data || {};
    },
  });
};

/**
 * Query hook to fetch order alerts
 * 
 * @param {Object} params - Query parameters
 * @returns {Object} React Query result with alerts data
 */
export const useOrderAlerts = (params = {}) => {
  return useQuery({
    queryKey: ['orders', 'alerts', params],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.ORDERS}/alerts`, { params });
      const data = response.data || {};
      return {
        alerts: Array.isArray(data.alerts) ? data.alerts : [],
        recommendations: Array.isArray(data.recommendations) ? data.recommendations : []
      };
    },
  });
};

/**
 * Query hook to fetch marketplaces list
 * 
 * @returns {Object} React Query result with marketplaces data
 */
export const useMarketplaces = () => {
  return useQuery({
    queryKey: ['orders', 'marketplaces'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.ORDERS}/marketplaces`);
      const data = response.data || {};
      return {
        marketplaces: Array.isArray(data.marketplaces) ? data.marketplaces : []
      };
    },
  });
};

/**
 * Hook to get returns data
 * @returns {Object} Query result with returns data
 */
export const useReturns = () => {
  return useQuery({
    queryKey: ['returns'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.RETURNS}`);
      return data;
    },
  });
};

/**
 * Hook to scan a return
 * @returns {Object} Mutation object for scanning returns
 */
export const useScanReturn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (scanData) => {
      const response = await api.post(`${API_ENDPOINTS.RETURNS}/scan`, scanData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });
};

/**
 * Hook to get next preparation order
 * @returns {Object} Query result with next preparation order
 */
export const useNextPreparation = () => {
  return useQuery({
    queryKey: ['orders', 'next-preparation'],
    queryFn: async () => {
      const response = await api.get('/api/v1/orders/next-preparation');
      return response.data;
    },
  });
};

/**
 * Mutation hook to start preparation for an order
 * @returns {Object} React Query mutation result
 */
export const useStartPreparation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderId) => {
      const response = await api.post(`${API_ENDPOINTS.ORDERS}/${orderId}/start-preparation`);
      return response.data;
    },
    onSuccess: (data, orderId) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['orders', orderId] });
    },
  });
};

export const useBackorders = (filters = {}) => {
  return useQuery({
    queryKey: ['backorders', filters],
    queryFn: async () => {
      const response = await api.get('/api/v1/orders/backorders', { params: filters });
      return response.data;
    },
  });
};

export const useBackorderDetails = (backorderId) => {
  return useQuery({
    queryKey: ['backorders', backorderId],
    queryFn: async () => {
      const response = await api.get(`/api/v1/orders/backorders/${backorderId}`);
      return response.data;
    },
    enabled: !!backorderId,
  });
};

export const useCreateBackorder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/orders/backorders', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backorders'] });
    },
  });
};

export const useFulfillBackorder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ backorderId, fulfillmentData }) => {
      const response = await api.post(`/api/v1/orders/backorders/${backorderId}/fulfill`, fulfillmentData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backorders'] });
    },
  });
};

export const useCancelBackorder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ backorderId, reason }) => {
      const response = await api.post(`/api/v1/orders/backorders/${backorderId}/cancel`, { reason });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backorders'] });
    },
  });
};

export const useBackorderQueue = (warehouseId, priority = 'all') => {
  return useQuery({
    queryKey: ['backorder-queue', warehouseId, priority],
    queryFn: async () => {
      const response = await api.get('/api/v1/orders/backorders/queue', {
        params: { warehouse_id: warehouseId, priority },
      });
      return response.data;
    },
    enabled: !!warehouseId,
  });
};

export const useExchanges = (filters = {}) => {
  return useQuery({
    queryKey: ['exchanges', filters],
    queryFn: async () => {
      const response = await api.get('/api/v1/orders/exchanges', { params: filters });
      return response.data;
    },
  });
};

export const useExchangeDetails = (exchangeId) => {
  return useQuery({
    queryKey: ['exchanges', exchangeId],
    queryFn: async () => {
      const response = await api.get(`/api/v1/orders/exchanges/${exchangeId}`);
      return response.data;
    },
    enabled: !!exchangeId,
  });
};

export const useCreateExchange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/orders/exchanges', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchanges'] });
    },
  });
};

export const useApproveExchange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (exchangeId) => {
      const response = await api.post(`/api/v1/orders/exchanges/${exchangeId}/approve`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchanges'] });
    },
  });
};

export const useRejectExchange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ exchangeId, reason }) => {
      const response = await api.post(`/api/v1/orders/exchanges/${exchangeId}/reject`, { reason });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchanges'] });
    },
  });
};

export const useProcessExchange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ exchangeId, processData }) => {
      const response = await api.post(`/api/v1/orders/exchanges/${exchangeId}/process`, processData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchanges'] });
    },
  });
};

/**
 * Mutation hook to export orders to various formats
 * 
 * Supports exporting orders to CSV, Excel, or PDF formats
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const exportOrders = useExportOrders();
 * exportOrders.mutate({ 
 *   format: 'csv', 
 *   filters: { status: 'completed' } 
 * });
 */
export const useExportOrders = () => {
  return useMutation({
    mutationFn: async ({ format = 'csv', filters = {} }) => {
      const response = await api.post(`${API_ENDPOINTS.ORDERS}/export`, {
        format,
        filters,
      }, {
        responseType: 'blob',
      });
      return response.data;
    },
  });
};

/**
 * Mutation hook to bulk delete multiple orders
 * 
 * Automatically invalidates orders queries on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const bulkDeleteOrders = useBulkDeleteOrders();
 * bulkDeleteOrders.mutate({ 
 *   orderIds: ['order_1', 'order_2', 'order_3'] 
 * });
 */
export const useBulkDeleteOrders = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderIds }) => {
      const response = await api.post(`${API_ENDPOINTS.ORDERS}/bulk-delete`, {
        order_ids: orderIds,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

/**
 * Mutation hook to bulk update multiple orders
 * 
 * Automatically invalidates orders queries on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const bulkUpdateOrders = useBulkUpdateOrders();
 * bulkUpdateOrders.mutate({ 
 *   orderIds: ['order_1', 'order_2'], 
 *   updates: { status: 'processing' } 
 * });
 */
export const useBulkUpdateOrders = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderIds, updates }) => {
      const response = await api.post(`${API_ENDPOINTS.ORDERS}/bulk-update`, {
        order_ids: orderIds,
        updates,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

/**
 * Mutation hook to bulk export multiple orders
 * 
 * Supports exporting multiple orders to various formats
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const bulkExportOrders = useBulkExportOrders();
 * bulkExportOrders.mutate({ 
 *   orderIds: ['order_1', 'order_2'], 
 *   format: 'pdf' 
 * });
 */
export const useBulkExportOrders = () => {
  return useMutation({
    mutationFn: async ({ orderIds, format = 'csv' }) => {
      const response = await api.post(`${API_ENDPOINTS.ORDERS}/bulk-export`, {
        order_ids: orderIds,
        format,
      }, {
        responseType: 'blob',
      });
      return response.data;
    },
  });
};

/**
 * Mutation hook to save order filter preferences
 * 
 * Automatically invalidates user preferences queries on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const saveOrderFilter = useSaveOrderFilter();
 * saveOrderFilter.mutate({ 
 *   filterName: 'My Filter', 
 *   filters: { status: 'pending', priority: 'high' } 
 * });
 */
export const useSaveOrderFilter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ filterName, filters }) => {
      const response = await api.post(`${API_ENDPOINTS.ORDERS}/filters`, {
        name: filterName,
        filters,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', 'filters'] });
    },
  });
};

/**
 * Mutation hook to delete saved order filter
 * 
 * Automatically invalidates user preferences queries on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const deleteOrderFilter = useDeleteOrderFilter();
 * deleteOrderFilter.mutate({ filterId: 'filter_123' });
 */
export const useDeleteOrderFilter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ filterId }) => {
      const response = await api.delete(`${API_ENDPOINTS.ORDERS}/filters/${filterId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', 'filters'] });
    },
  });
};

/**
 * Query hook to fetch saved order filters
 * 
 * @returns {Object} React Query result with saved filters
 * @property {Array} data - Array of saved filter objects
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useSavedOrderFilters = () => {
  return useQuery({
    queryKey: ['orders', 'filters'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.ORDERS}/filters`);
      return response.data || [];
    },
  });
};
